import { Card } from "@/ingot";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  useCart,
  useCatalog,
  useDeleteCartItem,
  useDeleteCartLine,
} from "@/api/queries";
import { useProcesses } from "@/api/queries/processes";
import type { CartItem, Process } from "@/api/types";
import { BulkHintBanner } from "@/components/cart/BulkHintBanner";
import { CartSelectionProvider } from "@/components/cart/CartSelectionContext";
import { CartOrderNoteSection } from "@/components/cart/CartOrderNoteSection";
import { CartAddonsSection } from "@/components/cart/CartAddonsSection";
import { CartSummaryV2 } from "@/components/cart/CartSummaryV2";
import { EmptyCartHero } from "@/components/cart/EmptyCartHero";
import { EmptyCartUpload } from "@/components/cart/EmptyCartUpload";
import { OperationSection } from "@/components/cart/OperationSection";
import { PriceListChangeNotice } from "@/components/cart/PriceListChangeNotice";

import { PageHero } from "@/components/ui/PageHero";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { createModule } from "@/processes/registry";
import type { FrontendModule } from "@/processes/types";

// Karta sestavy je LAZY ze stejného důvodu jako brána v ``CartSummaryV2``:
// košík je v eager entry chunku, který platí i ``/embed``, a kontejner
// v košíku má zlomek zákazníků. Renderuje se jen když nějaký existuje.
const AssemblyCard = lazy(() =>
  import("@/components/cart/AssemblyCard").then((m) => ({
    default: m.AssemblyCard,
  })),
);

const UNASSIGNED_SLUG = "unassigned";

interface ReorderSkipped {
  file_name: string;
  reason: string;
  code: string;
}

function useReorderNotice(): {
  skipped: ReorderSkipped[] | null;
  dismiss: () => void;
} {
  const [skipped, setSkipped] = useState<ReorderSkipped[] | null>(null);
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem("reorder_skipped");
      if (raw) setSkipped(JSON.parse(raw) as ReorderSkipped[]);
    } catch {
      // ignore
    }
  }, []);
  return {
    skipped,
    dismiss: () => {
      try {
        window.sessionStorage.removeItem("reorder_skipped");
      } catch {
        // ignore
      }
      setSkipped(null);
    },
  };
}

/** Pick the canonical process slug for grouping a CartItem in the UI. */
function primaryProcessFor(item: CartItem): string {
  const primary = item.operations.find((op) => op.order_index === 0);
  return primary?.process_type ?? UNASSIGNED_SLUG;
}

/**
 * Cart page — v2 redesign.
 *
 * Layout matches ``Objednavka v2.html``: left column groups items by
 * primary outbound process (laser / 3D tisk / ohýbání / …) into
 * collapsible ``OperationSection``s, right column renders a sticky
 * summary with per-operation breakdown. Variants of the same DXF live
 * inside a single ``PartCard``; per-line checkboxes power the
 * ``BulkBar`` once selection > 0 (apply actions land in PR3).
 *
 * Inline ``Finish`` dropdown on each PartCard is driven by the
 * tenant's SSoT operation graph — hidden unless the graph has a
 * primary → surface_finishes edge for the item's process.
 */
export default function Cart(): JSX.Element {
  const { t: tCart } = useTranslation("cart");
  const { t: tQuote } = useTranslation("quote");
  const { t: tA } = useTranslation("account");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const locale = lang === "cs" ? "cs-CZ" : "en-US";

  const cartQuery = useCart();
  const catalogQuery = useCatalog(lang);
  const deleteLine = useDeleteCartLine();
  const deleteItem = useDeleteCartItem();
  const { skipped, dismiss } = useReorderNotice();

  // ``useProcesses({ includeNonEntry })`` returns every module the
  // tenant can produce, with the icon_url / icon_source from PR1.
  const processes = useProcesses({ includeNonEntry: true });
  const moduleMap = useMemo(() => {
    const m = new Map<string, FrontendModule>();
    const rows = Array.isArray(processes.data) ? processes.data : [];
    for (const p of rows) m.set(p.name, createModule(p));
    return m;
  }, [processes.data]);

  const processBySlug = useMemo(() => {
    const m = new Map<string, Process>();
    const rows = Array.isArray(processes.data) ? processes.data : [];
    for (const p of rows) m.set(String(p.name), p);
    return m;
  }, [processes.data]);

  // KAN-1027 — rozpad ceny u řádku vypisuje i NAVAZUJÍCÍ operace, které
  // do sekce vlastního procesu nepatří. Popisek se proto resolvuje tady,
  // kde ceník procesů beztak leží, a posílá se dolů jako funkce; sekce
  // ani karta by ho jinak musely dotahovat podruhé.
  const processLabel = useCallback(
    (processType: string) =>
      processBySlug.get(processType)?.display_name ?? processType,
    [processBySlug],
  );

  // Stable grouping: iterate cart.items in order, append each item to
  // its primary-process bucket. Result is a list of [slug, items[]]
  // pairs in first-seen order so the UI doesn't reorder when the user
  // edits a line.
  // KAN-301 — kontejnery (sestava / kompletace) mají VLASTNÍ kartu
  // i s dětmi, takže do procesních sekcí nepatří ani ony, ani jejich
  // děti: kontejner nemá primární operaci (spadl by do koše
  // „Nezařazené") a dítě je vidět uvnitř své sestavy.
  const assemblies = useMemo(() => {
    const data = cartQuery.data;
    if (!data) return [] as Array<[CartItem, CartItem[]]>;
    const byId = new Map(data.items.map((it) => [it.id, it]));
    return data.items
      .filter((it) => (it.child_ids?.length ?? 0) > 0)
      .map(
        (container) =>
          [
            container,
            (container.child_ids ?? [])
              .map((id) => byId.get(id))
              .filter((it): it is CartItem => it !== undefined),
          ] as [CartItem, CartItem[]],
      );
  }, [cartQuery.data]);

  const groups = useMemo(() => {
    const data = cartQuery.data;
    if (!data) return [] as Array<[string, CartItem[]]>;
    const buckets = new Map<string, CartItem[]>();
    for (const item of data.items) {
      if (item.parent_id || (item.child_ids?.length ?? 0) > 0) continue;
      const slug = primaryProcessFor(item);
      const list = buckets.get(slug) ?? [];
      list.push(item);
      buckets.set(slug, list);
    }
    return Array.from(buckets.entries());
  }, [cartQuery.data]);

  const looksEmpty = !cartQuery.data || cartQuery.data.items.length === 0;
  // KAN-2093 — prázdný košík z cache (třeba po minulé objednávce), který
  // se právě načítá znovu, není „Košík je prázdný": po „Přidat do košíku"
  // tu ten nápis stál ~4 s, než dorazila nová data.
  const refreshingEmpty = looksEmpty && cartQuery.isFetching;
  const isEmpty = !cartQuery.isLoading && !refreshingEmpty && looksEmpty;

  return (
    <>
      <PageHero innerClassName="st-page-hero max-w-7xl px-6 pb-8 pt-10 md:pt-12">
        <h1 className="st-h2 text-3xl font-semibold tracking-tight text-ink">
          {tCart("title")}
        </h1>
      </PageHero>

      <main className="mx-auto max-w-7xl px-6 pb-16 pt-8">
        {skipped && skipped.length > 0 && (
          <Card padded className="mb-6 border-warn-border bg-warn-bg" data-testid="reorder-notice">
            <p className="text-sm text-ink">
              {tA("orders.reorderNotice.skipped", { count: skipped.length })}
            </p>
            <ul className="mt-2 list-disc pl-5 text-sm text-ink-2">
              {skipped.map((s, i) => (
                <li key={i}>
                  <span className="mono">{s.file_name}</span> — <span>{s.reason}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={dismiss}
              className="mono mt-2 text-[11px] uppercase tracking-[0.08em] text-ink-3 underline"
              data-testid="reorder-notice-dismiss"
            >
              {tA("orders.reorderNotice.dismiss")}
            </button>
          </Card>
        )}

        {(cartQuery.isLoading || refreshingEmpty) && (
          <p className="text-ink-3">{tQuote("price.loading")}</p>
        )}

        {isEmpty && (
          <>
            <EmptyCartHero />
            <Card padded elevation="raised" className="border-dashed border-border-strong text-center">
              <EmptyCartUpload lang={lang} />
            </Card>
          </>
        )}

        {!cartQuery.isLoading && cartQuery.data && cartQuery.data.items.length > 0 && (
          <CartSelectionProvider>
            <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0 space-y-4">
                <PriceListChangeNotice locale={locale} />
                <BulkHintBanner cart={cartQuery.data} locale={locale} />
                {assemblies.length > 0 && (
                  <Suspense fallback={null}>
                    {assemblies.map(([container, kids]) => (
                      <AssemblyCard
                        key={container.id}
                        container={container}
                        parts={kids}
                        locale={locale}
                      />
                    ))}
                  </Suspense>
                )}
                {groups.map(([slug, items]) => (
                  <OperationSection
                    key={slug}
                    processSlug={slug}
                    process={processBySlug.get(slug) ?? null}
                    items={items}
                    catalog={catalogQuery.data}
                    locale={locale}
                    lang={lang}
                    moduleMap={moduleMap}
                    onDeleteItem={(itemSlug) => deleteItem.mutate(itemSlug)}
                    onDeleteLine={(lineId) => deleteLine.mutate(lineId)}
                    removeItemLabel={tCart("removeItem")}
                    removeLineLabel={tCart("removeLine")}
                    editLineLabel={tCart("editLine")}
                    itemHref={(itemSlug) => `/${lang}/quote/${itemSlug}`}
                    maxExtraVariantsPerItem={
                      cartQuery.data.max_extra_variants_per_item ?? null
                    }
                    unassignedFallbackLabel={tCart("operationSection.unassigned", {
                      defaultValue: "Nezařazené (vyžaduje konfiguraci)",
                    })}
                    processLabel={processLabel}
                    currencyCode={cartQuery.data.currency_code}
                  />
                ))}
                <CartAddonsSection />
                <CartOrderNoteSection cart={cartQuery.data} />
              </div>
              <CartSummaryV2
                cart={cartQuery.data}
                lang={lang}
                locale={locale}
                primaryProcessFor={primaryProcessFor}
                processBySlug={processBySlug}
              />
            </div>
          </CartSelectionProvider>
        )}
      </main>
    </>
  );
}
